/**
 * SearchField Component
 *
 * Autocomplete search field for structures and KEBs with category filtering.
 */

import { getSearchIndex } from '../data/search.js';

/**
 * SearchField provides an autocomplete search interface with filters.
 */
export class SearchField {
  /**
   * Create search field
   * @param {string|HTMLElement} container - Container element or ID
   * @param {Object} cellMap - CellMap instance for navigation
   */
  constructor(container, cellMap) {
    this.container =
      typeof container === 'string'
        ? document.getElementById(container)
        : container;
    this.cellMap = cellMap;
    this.searchIndex = getSearchIndex();

    this.element = null;
    this.input = null;
    this.clearBtn = null;
    this.resultsList = null;
    this.filterToggle = null;
    this.filterCount = null;
    this.filtersPanel = null;
    this.typeChips = null;
    this.categoryChips = null;
    this.clearFiltersBtn = null;

    this.isOpen = false;
    this.filtersOpen = false;
    this.selectedIndex = -1;
    this.results = [];
    this.debounceTimer = null;

    // Active filters
    this.activeFilters = {
      type: null, // 'structure' or 'keb' or null
      categories: [] // array of category names
    };

    this.render();
    this.bindEvents();
    this.initFilters();
  }

  /**
   * Render the search field
   */
  render() {
    this.element = document.createElement('div');
    this.element.className = 'search-field';
    this.element.innerHTML = `
      <div class="search-input-wrapper">
        <span class="search-icon">🔍</span>
        <input
          type="text"
          class="search-input"
          placeholder="Struktur suchen..."
          aria-label="Struktur suchen"
          aria-autocomplete="list"
          aria-expanded="false"
          role="combobox"
        >
        <button class="search-filter-toggle" aria-label="Filter anzeigen">
          <span class="filter-icon">⚙️</span>
          <span class="filter-count" hidden>0</span>
        </button>
        <button class="search-clear" aria-label="Suche leeren" hidden>&times;</button>
      </div>
      <div class="search-filters" hidden>
        <div class="search-filter-section">
          <span class="search-filter-label">Typ:</span>
          <div class="search-filter-chips search-filter-type"></div>
        </div>
        <div class="search-filter-section">
          <span class="search-filter-label">Kategorie:</span>
          <div class="search-filter-chips search-filter-categories"></div>
        </div>
        <button class="search-filter-clear">Filter zurücksetzen</button>
      </div>
      <ul class="search-results" role="listbox" hidden></ul>
    `;

    this.input = this.element.querySelector('.search-input');
    this.clearBtn = this.element.querySelector('.search-clear');
    this.resultsList = this.element.querySelector('.search-results');
    this.filterToggle = this.element.querySelector('.search-filter-toggle');
    this.filterCount = this.element.querySelector('.filter-count');
    this.filtersPanel = this.element.querySelector('.search-filters');
    this.typeChips = this.element.querySelector('.search-filter-type');
    this.categoryChips = this.element.querySelector('.search-filter-categories');
    this.clearFiltersBtn = this.element.querySelector('.search-filter-clear');

    this.container.appendChild(this.element);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Input handling with debounce
    this.input.addEventListener('input', () => {
      this.clearBtn.hidden = !this.input.value;
      this.debounce(() => this.search(this.input.value), 150);
    });

    // Keyboard navigation
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Clear button
    this.clearBtn.addEventListener('click', () => this.clear());

    // Filter toggle
    this.filterToggle.addEventListener('click', () => this.toggleFilters());

    // Clear filters
    this.clearFiltersBtn.addEventListener('click', () => this.clearFilters());

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (!this.element.contains(e.target)) {
        this.closeDropdown();
        this.closeFilters();
      }
    });

    // Focus handling
    this.input.addEventListener('focus', () => {
      if (this.input.value && this.results.length > 0) {
        this.openDropdown();
      }
    });
  }

  /**
   * Initialize filter UI with categories
   */
  initFilters() {
    if (!this.searchIndex.isReady()) {
      return;
    }

    // Type filters
    const types = [
      { value: 'structure', label: '📍 Strukturen' },
      { value: 'keb', label: '💡 Wissensbuttons' }
    ];

    types.forEach((type) => {
      const chip = this.createChip(type.label, type.value, 'type');
      this.typeChips.appendChild(chip);
    });

    // Category filters (from search index)
    const categories = this.searchIndex.getCategories();
    categories.forEach((category) => {
      const chip = this.createChip(category, category, 'category');
      this.categoryChips.appendChild(chip);
    });
  }

  /**
   * Create a filter chip button
   * @param {string} label - Display label
   * @param {string} value - Filter value
   * @param {string} filterType - 'type' or 'category'
   * @returns {HTMLElement}
   */
  createChip(label, value, filterType) {
    const chip = document.createElement('button');
    chip.className = 'filter-chip';
    chip.textContent = label;
    chip.dataset.value = value;
    chip.dataset.filterType = filterType;

    chip.addEventListener('click', () => {
      if (filterType === 'type') {
        // Type is single-select: deselect others
        this.typeChips.querySelectorAll('.filter-chip').forEach((c) => {
          if (c !== chip) {
            c.classList.remove('filter-chip--active');
          }
        });
      }
      chip.classList.toggle('filter-chip--active');
      this.updateActiveFilters();
      this.search(this.input.value);
    });

    return chip;
  }

  /**
   * Update active filters from chip states
   */
  updateActiveFilters() {
    // Type filter (single select)
    const activeType = this.typeChips.querySelector('.filter-chip--active');
    this.activeFilters.type = activeType ? activeType.dataset.value : null;

    // Category filters (multi select)
    const activeCategories = this.categoryChips.querySelectorAll(
      '.filter-chip--active'
    );
    this.activeFilters.categories = Array.from(activeCategories).map(
      (c) => c.dataset.value
    );

    // Update count badge
    const count =
      (this.activeFilters.type ? 1 : 0) + this.activeFilters.categories.length;
    this.filterCount.textContent = count;
    this.filterCount.hidden = count === 0;
  }

  /**
   * Toggle filters panel visibility
   */
  toggleFilters() {
    this.filtersOpen = !this.filtersOpen;
    this.filtersPanel.hidden = !this.filtersOpen;
    this.filterToggle.classList.toggle(
      'search-filter-toggle--active',
      this.filtersOpen
    );

    // Close results dropdown when opening filters
    if (this.filtersOpen) {
      this.closeDropdown();
    }
  }

  /**
   * Close filters panel
   */
  closeFilters() {
    this.filtersOpen = false;
    this.filtersPanel.hidden = true;
    this.filterToggle.classList.remove('search-filter-toggle--active');
  }

  /**
   * Clear all active filters
   */
  clearFilters() {
    this.element.querySelectorAll('.filter-chip--active').forEach((chip) => {
      chip.classList.remove('filter-chip--active');
    });
    this.activeFilters = { type: null, categories: [] };
    this.filterCount.hidden = true;
    this.search(this.input.value);
  }

  /**
   * Get active filter count
   * @returns {number}
   */
  getActiveFilterCount() {
    return (
      (this.activeFilters.type ? 1 : 0) + this.activeFilters.categories.length
    );
  }

  /**
   * Perform search with active filters
   * @param {string} query - Search query
   */
  search(query) {
    if (!query || query.length < 2) {
      this.closeDropdown();
      return;
    }

    if (!this.searchIndex.isReady()) {
      return;
    }

    // Build search options with type filter
    const options = { limit: 12 };
    if (this.activeFilters.type) {
      options.type = this.activeFilters.type;
    }

    // Search with base options
    let results = this.searchIndex.search(query, options);

    // Apply category filter (client-side since Fuse doesn't support multi-value)
    if (this.activeFilters.categories.length > 0) {
      results = results.filter((r) =>
        this.activeFilters.categories.includes(r.category)
      );
    }

    // Limit final results
    this.results = results.slice(0, 8);
    this.selectedIndex = -1;

    if (this.results.length > 0) {
      this.renderResults();
      this.openDropdown();
    } else {
      this.renderNoResults();
      this.openDropdown();
    }
  }

  /**
   * Render search results
   */
  renderResults() {
    this.resultsList.innerHTML = this.results
      .map(
        (result, index) => `
      <li
        class="search-result"
        role="option"
        data-index="${index}"
        aria-selected="${index === this.selectedIndex}"
      >
        <span class="search-result-type search-result-type--${result.type}">
          ${result.type === 'structure' ? '📍' : '💡'}
        </span>
        <div class="search-result-content">
          <span class="search-result-name">${this.escapeHtml(result.name)}</span>
          <span class="search-result-category">${this.escapeHtml(result.category || '')}</span>
        </div>
      </li>
    `
      )
      .join('');

    // Add click handlers
    this.resultsList.querySelectorAll('.search-result').forEach((li) => {
      li.addEventListener('click', () => {
        const index = parseInt(li.dataset.index);
        this.selectResult(index);
      });
    });
  }

  /**
   * Render no results message
   */
  renderNoResults() {
    const hasFilters = this.getActiveFilterCount() > 0;
    this.resultsList.innerHTML = `
      <li class="search-no-results">
        Keine Ergebnisse gefunden${hasFilters ? ' mit diesen Filtern' : ''}
      </li>
    `;
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} e - Keyboard event
   */
  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.moveSelection(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.moveSelection(-1);
        break;
      case 'Enter':
        e.preventDefault();
        if (this.selectedIndex >= 0) {
          this.selectResult(this.selectedIndex);
        }
        break;
      case 'Escape':
        this.closeDropdown();
        this.closeFilters();
        this.input.blur();
        break;
    }
  }

  /**
   * Move selection up or down
   * @param {number} direction - 1 for down, -1 for up
   */
  moveSelection(direction) {
    if (!this.isOpen || this.results.length === 0) {
      return;
    }

    const newIndex = this.selectedIndex + direction;
    if (newIndex >= -1 && newIndex < this.results.length) {
      this.selectedIndex = newIndex;
      this.updateSelectedStyle();
    }
  }

  /**
   * Update selected item styling
   */
  updateSelectedStyle() {
    this.resultsList.querySelectorAll('.search-result').forEach((li, index) => {
      li.classList.toggle(
        'search-result--selected',
        index === this.selectedIndex
      );
      li.setAttribute('aria-selected', index === this.selectedIndex);
    });

    // Scroll into view
    if (this.selectedIndex >= 0) {
      const selected = this.resultsList.children[this.selectedIndex];
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  /**
   * Select a result and navigate
   * @param {number} index - Result index
   */
  selectResult(index) {
    const result = this.results[index];
    if (!result) {
      return;
    }

    // Navigate based on type
    if (result.type === 'structure') {
      const clickHandler = this.cellMap.getClickHandler();
      if (clickHandler) {
        clickHandler.selectById(result.id);
      }
    } else if (result.type === 'keb') {
      // For now, just zoom to the KEB position
      // Full KEB handling will be in Issue #018
      this.cellMap.zoomTo(result.position.x, result.position.y, result.resolution);
    }

    // Update input and close
    this.input.value = result.name;
    this.closeDropdown();
    this.closeFilters();
    this.input.blur();
  }

  /**
   * Open dropdown
   */
  openDropdown() {
    this.isOpen = true;
    this.resultsList.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.element.classList.add('search-field--open');
  }

  /**
   * Close dropdown
   */
  closeDropdown() {
    this.isOpen = false;
    this.resultsList.hidden = true;
    this.selectedIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.element.classList.remove('search-field--open');
  }

  /**
   * Clear search
   */
  clear() {
    this.input.value = '';
    this.results = [];
    this.closeDropdown();
    this.clearBtn.hidden = true;
    this.input.focus();
  }

  /**
   * Debounce function calls
   * @param {Function} fn - Function to debounce
   * @param {number} delay - Delay in ms
   */
  debounce(fn, delay) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(fn, delay);
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Focus the search input
   */
  focus() {
    this.input.focus();
  }

  /**
   * Get the element
   * @returns {HTMLElement}
   */
  getElement() {
    return this.element;
  }
}
